import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Shield,
  Server,
  AlertTriangle,
  Activity,
  TrendingUp,
  TrendingDown,
  Zap,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Globe,
  Brain,
  Bell,
  X,
  Eye,
  Target,
  ShieldCheck,
  Bug,
  Radar,
  BarChart3,
  FileSearch,
  Lock,
  Crosshair,
  RefreshCw,
  Workflow,
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, RadialBarChart, RadialBar,
} from 'recharts';

export default function Dashboard() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [bsecMetrics, setBsecMetrics] = useState(null);
  const [operationalData, setOperationalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [automationData, setAutomationData] = useState(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    fetchBsecMetrics();
    fetchOperationalData();
    fetchNotifications();
    fetchAutomationData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBsecMetrics = async () => {
    try {
      const response = await api.get('/dashboard/bsec-metrics');
      setBsecMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch BSEC metrics:', error);
      toast.error('Failed to fetch dashboard metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperationalData = async () => {
    try {
      const response = await api.get('/dashboard/operational');
      setOperationalData(response.data);
    } catch (error) {
      console.error('Failed to fetch operational data:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications/unified');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchAutomationData = async () => {
    try {
      const [statusRes, basRes, actionRes] = await Promise.all([
        api.get('/automation/status').catch(() => ({ data: null })),
        api.get('/bas/simulations?limit=10').catch(() => ({ data: { simulations: [] } })),
        api.get('/automation/action-log?limit=10').catch(() => ({ data: { logs: [] } }))
      ]);
      
      setAutomationData({
        status: statusRes.data,
        recentSimulations: basRes.data.simulations || [],
        recentActions: actionRes.data.logs || []
      });
    } catch (error) {
      console.error('Failed to fetch automation data:', error);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-400 bg-red-500/20',
      high: 'text-orange-400 bg-orange-500/20',
      medium: 'text-yellow-400 bg-yellow-500/20',
      low: 'text-green-400 bg-green-500/20',
      info: 'text-sky-400 bg-sky-500/20',
    };
    return colors[severity] || colors.info;
  };

  const handleNotificationClick = (notif) => {
    if (notif.link) navigate(notif.link);
    setShowNotifications(false);
  };

  const handleTileClick = (path, filter = null) => {
    if (filter) {
      navigate(path, { state: { filter } });
    } else {
      navigate(path);
    }
  };

  // Design System Colors
  const chartColors = {
    primary: isDarkMode ? '#00BFFF' : '#00A5E0',
    secondary: isDarkMode ? '#4ECDC4' : '#14B8A6',
    critical: '#DC2626',
    high: '#EA580C',
    medium: '#CA8A04',
    low: '#16A34A',
    grid: isDarkMode ? '#2A374F' : '#E2E8F0',
    text: isDarkMode ? '#94A3B8' : '#64748B',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? 'border-electric-500' : 'border-electric-600'}`}></div>
          <span className={`text-sm font-inter ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading BSEC Dashboard...</span>
        </div>
      </div>
    );
  }

  const ev = bsecMetrics?.exposure_visibility || {};
  const rp = bsecMetrics?.risk_prioritization || {};
  const dr = bsecMetrics?.detection_response || {};
  const rr = bsecMetrics?.remediation_resilience || {};
  const ti = bsecMetrics?.threat_intelligence || {};
  const ac = bsecMetrics?.assessment_center || {};
  const comp = bsecMetrics?.compliance || {};
  const vulnBreakdown = rp.vulnerability_breakdown || {};

  // Chart Data
  const hygieneGaugeData = [{ 
    name: 'Hygiene', 
    value: rp.cyber_hygiene_score || 0, 
    fill: (rp.cyber_hygiene_score || 0) >= 80 ? chartColors.low : (rp.cyber_hygiene_score || 0) >= 60 ? chartColors.medium : chartColors.critical 
  }];
  
  const riskGaugeData = [{ 
    name: 'Risk', 
    value: rp.prioritized_risk_score || 0, 
    fill: (rp.prioritized_risk_score || 0) >= 70 ? chartColors.critical : (rp.prioritized_risk_score || 0) >= 50 ? chartColors.medium : chartColors.low 
  }];

  const vulnSeverityData = [
    { name: 'Critical', value: vulnBreakdown.critical || 0, color: chartColors.critical },
    { name: 'High', value: vulnBreakdown.high || 0, color: chartColors.high },
    { name: 'Medium', value: vulnBreakdown.medium || 0, color: chartColors.medium },
    { name: 'Low', value: vulnBreakdown.low || 0, color: chartColors.low },
  ];

  const riskTrendData = rr.risk_reduction_trend || [];
  const complianceData = (comp.by_standard || []).slice(0, 6);

  // Metric Card Component with Professional Styling
  const MetricCard = ({ title, value, subtitle, icon: Icon, color, onClick, trend, trendValue }) => (
    <div 
      onClick={onClick}
      className={`metric-card cursor-pointer group ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
            isDarkMode 
              ? `bg-${color}-500/15 border border-${color}-500/30 group-hover:bg-${color}-500/25 group-hover:shadow-neon-${color}` 
              : `bg-${color}-100 border border-${color}-200 group-hover:bg-${color}-150`
          }`}>
            <Icon className={`w-4 h-4 ${isDarkMode ? `text-${color}-400` : `text-${color}-600`}`} />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-[11px] font-medium ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              <span className="font-mono">{trendValue}</span>
            </div>
          )}
        </div>
        <h3 className={`font-inter text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{title}</h3>
        <p className={`font-inter text-2xl font-semibold tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</p>
        {subtitle && <p className={`text-[11px] font-inter mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{subtitle}</p>}
      </div>
    </div>
  );

  // Section Header Component
  const SectionHeader = ({ icon: Icon, title, color }) => (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
        isDarkMode 
          ? `bg-${color}-500/15 border border-${color}-500/30` 
          : `bg-${color}-100 border border-${color}-200`
      }`}>
        <Icon className={`w-4 h-4 ${isDarkMode ? `text-${color}-400` : `text-${color}-600`}`} />
      </div>
      <h2 className={`font-inter font-semibold text-base tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
    </div>
  );

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`px-3 py-2 rounded-lg text-xs font-inter ${
          isDarkMode 
            ? 'bg-navy-800 border border-navy-700 text-white shadow-lg' 
            : 'bg-white border border-slate-200 text-slate-900 shadow-md'
        }`}>
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="mt-1">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="bsec-dashboard" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`font-inter font-bold text-2xl sm:text-3xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            BSEC Security Dashboard
          </h1>
          <p className={`font-inter text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            CTEM-Aligned Metrics: Scoping → Discovery → Prioritization → Validation → Mobilization
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => fetchBsecMetrics()}
            className={`glass px-3 py-2 rounded-lg transition-all flex items-center gap-2 text-sm font-inter ${
              isDarkMode ? 'hover:border-electric-500/50 hover:shadow-neon-electric' : 'hover:border-electric-500 hover:shadow-card-hover'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>Refresh</span>
          </button>
          
          <div className={`flex items-center gap-2 glass px-3 py-2 rounded-lg`}>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className={`font-inter text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Live</span>
          </div>
          
          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button 
              data-testid="notifications-bell"
              onClick={() => setShowNotifications(!showNotifications)}
              className={`relative glass px-3 py-2 rounded-lg transition-all ${
                isDarkMode ? 'hover:border-electric-500/50' : 'hover:border-electric-500'
              }`}
            >
              <Bell className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </button>
            {showNotifications && (
              <div className={`absolute right-0 top-full mt-2 w-80 max-h-[60vh] overflow-y-auto rounded-xl border shadow-2xl z-50 ${
                isDarkMode 
                  ? 'bg-navy-900/98 border-navy-700 backdrop-blur-xl' 
                  : 'bg-white/98 border-slate-200 backdrop-blur-xl'
              }`}>
                <div className={`sticky top-0 flex items-center justify-between p-3 border-b ${
                  isDarkMode ? 'border-navy-700 bg-navy-900/98' : 'border-slate-200 bg-white/98'
                }`}>
                  <h3 className={`font-inter font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Notifications</h3>
                  <button onClick={() => setShowNotifications(false)} className={`p-1 rounded hover:bg-white/10 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {notifications.length === 0 ? (
                  <div className={`p-6 text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm font-inter">No notifications yet</p>
                  </div>
                ) : (
                  <div className={`divide-y ${isDarkMode ? 'divide-navy-700' : 'divide-slate-100'}`}>
                    {notifications.slice(0, 8).map((notif) => (
                      <button key={notif.id} onClick={() => handleNotificationClick(notif)} className={`w-full text-left p-3 transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                        <div className="flex items-start gap-3">
                          <div className={`p-1.5 rounded-lg ${getSeverityColor(notif.severity)}`}>
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-inter text-xs font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{notif.title}</p>
                            <p className={`font-inter text-[11px] mt-0.5 line-clamp-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{notif.message}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========== EXPOSURE & VISIBILITY METRICS ========== */}
      <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
        <SectionHeader icon={Eye} title="Exposure & Visibility Metrics" color="sky" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            title="Asset Discovery Coverage"
            value={`${ev.automated_asset_discovery_coverage || 0}%`}
            subtitle="Auto-discovered assets"
            icon={Radar}
            color="sky"
            onClick={() => handleTileClick('/asset-management')}
          />
          <MetricCard
            title="EASM Exposure Rate"
            value={`${ev.easm_exposure_rate || 0}%`}
            subtitle={`${ev.high_exposure_assets || 0} high-exposure`}
            icon={Globe}
            color="orange"
            onClick={() => handleTileClick('/easm')}
          />
          <MetricCard
            title="Asset Visibility Score"
            value={`${ev.asset_visibility_score || 0}%`}
            subtitle="Inventory completeness"
            icon={Eye}
            color="violet"
            onClick={() => handleTileClick('/asset-management')}
          />
          <MetricCard
            title="Vulnerability Density"
            value={ev.vulnerability_density || 0}
            subtitle="Vulns per asset"
            icon={Bug}
            color="red"
            onClick={() => handleTileClick('/vulnerability-hub')}
          />
        </div>
      </div>

      {/* ========== THREAT INTELLIGENCE, ASSESSMENT, COMPLIANCE ========== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Threat Intelligence */}
        <div 
          onClick={() => handleTileClick('/threat-intelligence')}
          className={`rounded-xl p-5 cursor-pointer transition-all hover:scale-[1.01] ${
            isDarkMode 
              ? 'bg-navy-800/50 border border-navy-700 hover:border-electric-500/50 hover:shadow-neon-electric' 
              : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover hover:border-electric-500'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-violet-500/15 border border-violet-500/30' : 'bg-violet-100 border border-violet-200'}`}>
              <Brain className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
            </div>
            <div>
              <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Threat Intelligence</h3>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>IOCs, Pulses & Alerts</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'IOCs', value: ti.total_iocs || 0, color: 'orange' },
              { label: 'Pulses', value: ti.threat_pulses || 0, color: 'violet' },
              { label: 'Brand Scans', value: ti.brand_scans || 0, color: 'sky' },
              { label: 'Active Alerts', value: ti.active_alerts || 0, color: 'red' },
            ].map((item) => (
              <div key={item.label} className={`text-center p-2 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`font-inter text-lg font-bold text-${item.color}-${isDarkMode ? '400' : '600'}`}>{item.value}</p>
                <p className={`text-[10px] font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Assessment Center */}
        <div 
          onClick={() => handleTileClick('/assessment-center')}
          className={`rounded-xl p-5 cursor-pointer transition-all hover:scale-[1.01] ${
            isDarkMode 
              ? 'bg-navy-800/50 border border-navy-700 hover:border-electric-500/50 hover:shadow-neon-electric' 
              : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover hover:border-electric-500'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/15 border border-indigo-500/30' : 'bg-indigo-100 border border-indigo-200'}`}>
              <Activity className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <div>
              <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Assessment Center</h3>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Scans & Validation</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Total', value: ac.total || 0, color: 'indigo' },
              { label: 'Completed', value: ac.completed || 0, color: 'emerald' },
              { label: 'Running', value: ac.running || 0, color: 'yellow' },
              { label: 'Success Rate', value: `${ac.success_rate || 0}%`, color: 'sky' },
            ].map((item) => (
              <div key={item.label} className={`text-center p-2 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`font-inter text-lg font-bold text-${item.color}-${isDarkMode ? '400' : '600'}`}>{item.value}</p>
                <p className={`text-[10px] font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance */}
        <div 
          onClick={() => handleTileClick('/compliance')}
          className={`rounded-xl p-5 cursor-pointer transition-all hover:scale-[1.01] ${
            isDarkMode 
              ? 'bg-navy-800/50 border border-navy-700 hover:border-electric-500/50 hover:shadow-neon-electric' 
              : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover hover:border-electric-500'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200'}`}>
              <Zap className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Compliance Score</h3>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{comp.total_checks || 0} checks</p>
            </div>
          </div>
          <div className="relative mb-4">
            <div className="flex items-end justify-center h-14">
              <div className={`text-4xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {comp.overall_score || 0}
                <span className={`text-sm font-normal ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>%</span>
              </div>
            </div>
            <div className={`w-full h-2 rounded-full overflow-hidden mt-3 ${isDarkMode ? 'bg-navy-700' : 'bg-slate-200'}`}>
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500"
                style={{ width: `${comp.overall_score || 0}%` }}
              ></div>
            </div>
          </div>
          {complianceData.length > 0 && (
            <div className="space-y-2">
              {complianceData.slice(0, 3).map((std, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <div className={`w-14 font-inter truncate ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{std.standard}</div>
                  <div className="flex-1">
                    <div className={`h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-navy-700' : 'bg-slate-200'}`}>
                      <div className={`h-full rounded-full ${std.score >= 80 ? 'bg-emerald-500' : std.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${std.score}%` }}></div>
                    </div>
                  </div>
                  <div className={`w-10 text-right font-inter font-semibold ${std.score >= 80 ? 'text-emerald-500' : std.score >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>{std.score}%</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ========== REMEDIATION PROGRESS & OSINT ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`lg:col-span-2 rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200'}`}>
                <CheckCircle className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              </div>
              <div>
                <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation Progress</h3>
                <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Vulnerability resolution tracking</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/vulnerability-hub')}
              className={isDarkMode ? 'border-white/10 hover:bg-white/5' : ''}
            >
              View Details
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{vulnBreakdown.open || 0}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Open Issues</p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
              <p className={`text-2xl font-bold text-blue-500`}>{vulnBreakdown.in_progress || 0}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>In Progress</p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
              <p className={`text-2xl font-bold text-emerald-500`}>{vulnBreakdown.closed || 0}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Resolved</p>
            </div>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {vulnBreakdown.closed && (vulnBreakdown.open + vulnBreakdown.in_progress + vulnBreakdown.closed) > 0
                  ? Math.round((vulnBreakdown.closed / (vulnBreakdown.open + vulnBreakdown.in_progress + vulnBreakdown.closed)) * 100)
                  : 0}%
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Resolution Rate</p>
            </div>
          </div>
          <div className="mt-4">
            <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
              <div className="flex h-full">
                <div className="bg-emerald-500 h-full transition-all" style={{width: `${vulnBreakdown.closed && (vulnBreakdown.open + vulnBreakdown.in_progress + vulnBreakdown.closed) > 0 ? (vulnBreakdown.closed / (vulnBreakdown.open + vulnBreakdown.in_progress + vulnBreakdown.closed)) * 100 : 0}%`}}></div>
                <div className="bg-blue-500 h-full transition-all" style={{width: `${vulnBreakdown.in_progress && (vulnBreakdown.open + vulnBreakdown.in_progress + vulnBreakdown.closed) > 0 ? (vulnBreakdown.in_progress / (vulnBreakdown.open + vulnBreakdown.in_progress + vulnBreakdown.closed)) * 100 : 0}%`}}></div>
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs">
              <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Progress: Resolved + In Progress</span>
              <span className="text-emerald-500">
                {(vulnBreakdown.closed || 0) + (vulnBreakdown.in_progress || 0)} / {(vulnBreakdown.open || 0) + (vulnBreakdown.in_progress || 0) + (vulnBreakdown.closed || 0)} vulnerabilities
              </span>
            </div>
          </div>
        </div>
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-red-500/15 border border-red-500/30' : 'bg-red-100 border border-red-200'}`}>
              <Globe className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            </div>
            <div>
              <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>OSINT Coverage</h3>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Active Integrations</p>
            </div>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {[
              { name: 'VirusTotal', icon: Bug, desc: 'Domain reputation', color: 'blue' },
              { name: 'Shodan', icon: Server, desc: 'Infrastructure exposure', color: 'orange' },
              { name: 'Censys', icon: Lock, desc: 'Certificate transparency', color: 'violet' },
              { name: 'AbuseIPDB', icon: AlertTriangle, desc: 'IP reputation', color: 'red' },
              { name: 'MalwareBazaar', icon: Bug, desc: 'Malware samples', color: 'pink' },
              { name: 'URLhaus', icon: Globe, desc: 'Malicious URLs', color: 'yellow' },
              { name: 'PhishTank', icon: AlertTriangle, desc: 'Phishing detection', color: 'cyan' },
              { name: 'GreyNoise', icon: Radar, desc: 'Internet noise', color: 'emerald' },
            ].map((service) => (
              <div key={service.name} className={`flex items-center gap-3 p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <service.icon className={`w-4 h-4 text-${service.color}-500`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{service.name}</p>
                  <p className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{service.desc}</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Active"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'Assets', value: ev.total_assets || 0, icon: Server, color: 'indigo', path: '/assets' },
          { label: 'Open Vulns', value: vulnBreakdown.open || 0, icon: Bug, color: 'orange', path: '/vulnerability-hub' },
          { label: 'Critical', value: vulnBreakdown.critical || 0, icon: AlertCircle, color: 'red', path: '/vulnerability-hub', filter: { severity: 'critical' } },
          { label: 'High', value: vulnBreakdown.high || 0, icon: AlertTriangle, color: 'orange', path: '/vulnerability-hub', filter: { severity: 'high' } },
          { label: 'Threats', value: dr.active_threats || 0, icon: Crosshair, color: 'red', path: '/threats' },
          { label: 'Scans', value: ac.running || 0, icon: Activity, color: 'emerald', path: '/assessment-center' },
          { label: 'Closed', value: vulnBreakdown.closed || 0, icon: CheckCircle, color: 'emerald' },
          { label: 'EASM', value: ev.easm_assets || 0, icon: Globe, color: 'sky', path: '/asset-management' },
        ].map((item) => (
          <div 
            key={item.label}
            onClick={() => item.path && handleTileClick(item.path, item.filter)}
            className={`rounded-xl p-3 text-center cursor-pointer transition-all hover:scale-[1.02] ${
              isDarkMode 
                ? 'bg-navy-800/40 border border-navy-700 hover:border-electric-500/30' 
                : 'bg-white border border-slate-200 shadow-sm hover:shadow-card'
            }`}
          >
            <item.icon className={`w-4 h-4 mx-auto mb-2 ${isDarkMode ? `text-${item.color}-400` : `text-${item.color}-600`}`} />
            <p className={`text-lg font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.value}</p>
            <p className={`text-[10px] font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{item.label}</p>
          </div>
        ))}
      </div>

      {/* ========== 2. RISK PRIORITIZATION & HYGIENE METRICS ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cyber Hygiene Score Gauge */}
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <SectionHeader icon={ShieldCheck} title="Cyber Hygiene Score" color="emerald" />
          <ResponsiveContainer width="100%" height={180}>
            <RadialBarChart cx="50%" cy="50%" innerRadius="65%" outerRadius="95%" barSize={14} data={hygieneGaugeData} startAngle={180} endAngle={0}>
              <RadialBar background={{ fill: isDarkMode ? '#1A2338' : '#F1F5F9' }} dataKey="value" cornerRadius={10} />
              <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" className={`font-inter font-bold`} style={{ fontSize: '32px', fill: isDarkMode ? '#F1F5F9' : '#0F172A' }}>
                {rp.cyber_hygiene_score || 0}
              </text>
              <text x="50%" y="62%" textAnchor="middle" dominantBaseline="middle" className="font-inter" style={{ fontSize: '12px', fill: isDarkMode ? '#64748B' : '#64748B' }}>
                / 100
              </text>
            </RadialBarChart>
          </ResponsiveContainer>
          <div className={`text-center text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Patching + Config + Identity + Exposure
          </div>
        </div>

        {/* Prioritized Risk Score Gauge */}
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <SectionHeader icon={Target} title="Prioritized Risk Score" color="red" />
          <ResponsiveContainer width="100%" height={180}>
            <RadialBarChart cx="50%" cy="50%" innerRadius="65%" outerRadius="95%" barSize={14} data={riskGaugeData} startAngle={180} endAngle={0}>
              <RadialBar background={{ fill: isDarkMode ? '#1A2338' : '#F1F5F9' }} dataKey="value" cornerRadius={10} />
              <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" className="font-inter font-bold" style={{ fontSize: '32px', fill: isDarkMode ? '#F1F5F9' : '#0F172A' }}>
                {rp.prioritized_risk_score || 0}
              </text>
              <text x="50%" y="62%" textAnchor="middle" dominantBaseline="middle" className="font-inter font-medium" style={{ 
                fontSize: '11px', 
                fill: rp.risk_level === 'critical' ? chartColors.critical : rp.risk_level === 'high' ? chartColors.high : rp.risk_level === 'medium' ? chartColors.medium : chartColors.low 
              }}>
                {(rp.risk_level || 'low').toUpperCase()}
              </text>
            </RadialBarChart>
          </ResponsiveContainer>
          <div className={`text-center text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            CVSS + Threat Intel + Business Impact
          </div>
        </div>

        {/* Critical Metrics */}
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <SectionHeader icon={AlertTriangle} title="Critical Exposures" color="orange" />
          <div className="space-y-3 mt-4">
            <div className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
              <span className={`text-sm font-inter ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Critical Exposure Count</span>
              <span className={`text-xl font-inter font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{rp.critical_exposure_count || 0}</span>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-orange-50 border border-orange-200'}`}>
              <span className={`text-sm font-inter ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Exploitable Ratio</span>
              <span className={`text-xl font-inter font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{rp.exploitable_exposure_ratio || 0}%</span>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-200'}`}>
              <span className={`text-sm font-inter ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Total Vulnerabilities</span>
              <span className={`text-xl font-inter font-bold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>{vulnBreakdown.total || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Vulnerability Breakdown & Risk Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Severity Distribution */}
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <h3 className={`font-inter font-semibold text-base mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Vulnerability Severity Distribution</h3>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {vulnSeverityData.map((item) => (
              <div 
                key={item.name}
                onClick={() => handleTileClick('/vulnerability-hub', { severity: item.name.toLowerCase() })}
                className={`p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.02]`}
                style={{ 
                  backgroundColor: isDarkMode ? `${item.color}15` : `${item.color}10`, 
                  borderLeft: `3px solid ${item.color}` 
                }}
              >
                <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{item.name}</p>
                <p className="text-xl font-inter font-bold" style={{ color: item.color }}>{item.value}</p>
              </div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={vulnSeverityData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} opacity={0.5} />
              <XAxis dataKey="name" stroke={chartColors.text} style={{ fontSize: '11px', fontFamily: 'Inter' }} />
              <YAxis stroke={chartColors.text} style={{ fontSize: '11px', fontFamily: 'Inter' }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {vulnSeverityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Risk Reduction Trend */}
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <h3 className={`font-inter font-semibold text-base mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Risk Reduction Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={riskTrendData}>
              <defs>
                <linearGradient id="riskGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} opacity={0.5} />
              <XAxis dataKey="month" stroke={chartColors.text} style={{ fontSize: '11px', fontFamily: 'Inter' }} />
              <YAxis stroke={chartColors.text} style={{ fontSize: '11px', fontFamily: 'Inter' }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="risk_score" stroke={chartColors.primary} strokeWidth={2} fill="url(#riskGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ========== DETECTION & RESPONSE EFFICIENCY ========== */}
      <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
        <SectionHeader icon={Clock} title="Detection & Response Efficiency Metrics" color="violet" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricCard title="Mean Time to Detect" value={`${dr.mttd_hours || 0}h`} subtitle="MTTD" icon={Eye} color="indigo" />
          <MetricCard title="Mean Time to Validate" value={`${dr.mttv_hours || 0}h`} subtitle="MTTV" icon={FileSearch} color="violet" />
          <MetricCard title="Mean Time to Remediate" value={`${dr.mttr_days || 0}d`} subtitle="MTTR" icon={Clock} color="pink" />
          <MetricCard title="False Positive Rate" value={`${dr.false_positive_rate || 0}%`} subtitle="Accuracy" icon={XCircle} color="yellow" />
          <MetricCard title="Active Threats" value={dr.active_threats || 0} subtitle="Requiring action" icon={AlertTriangle} color="red" onClick={() => handleTileClick('/threats')} />
        </div>
      </div>

      {/* ========== REMEDIATION & RESILIENCE ========== */}
      <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
        <SectionHeader icon={ShieldCheck} title="Remediation & Resilience Metrics" color="emerald" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard title="Remediation Rate" value={`${rr.remediation_rate || 0}%`} subtitle="Closed within SLA" icon={CheckCircle} color="emerald" trend="up" trendValue="+5%" />
          <MetricCard title="Defense Validation" value={`${rr.defense_validation_rate || 0}%`} subtitle="Attacks blocked" icon={Shield} color="blue" />
          <MetricCard title="Attack Paths" value={rr.attack_paths_count || 0} subtitle="Viable paths to assets" icon={Crosshair} color="orange" onClick={() => handleTileClick('/attack-paths')} />
          <MetricCard title="Crown Jewels Protected" value={rr.crown_jewels_protected || 0} subtitle="Critical assets" icon={Lock} color="violet" onClick={() => handleTileClick('/crown-jewels')} />
        </div>
      </div>

      {/* ========== REMEDIATION FACTORY (OPERATIONAL) ========== */}
      {operationalData?.remediation_factory && (
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-4">
            <SectionHeader icon={Workflow} title="Remediation Factory" color="violet" />
            <button 
              onClick={() => navigate('/remediation-factory')}
              className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'text-violet-400 hover:bg-violet-500/10' 
                  : 'text-violet-600 hover:bg-violet-50'
              }`}
            >
              View All →
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
            <MetricCard 
              title="Total Tasks" 
              value={operationalData.remediation_factory.total_tasks || 0} 
              subtitle="Active remediation" 
              icon={Workflow} 
              color="violet"
              onClick={() => handleTileClick('/remediation-factory')}
            />
            <MetricCard 
              title="Pending Approval" 
              value={operationalData.remediation_factory.pending_approvals || 0} 
              subtitle="Awaiting review" 
              icon={Clock} 
              color="yellow"
              onClick={() => handleTileClick('/remediation-factory')}
            />
            <MetricCard 
              title="Deployed Today" 
              value={operationalData.remediation_factory.deployed_today || 0} 
              subtitle="Fixes applied" 
              icon={CheckCircle} 
              color="emerald"
            />
            <MetricCard 
              title="Remediation Rate" 
              value={`${operationalData.remediation_factory.remediation_rate || 0}%`} 
              subtitle="Success rate" 
              icon={TrendingUp} 
              color="sky"
            />
            <MetricCard 
              title="AI Fixes" 
              value={operationalData.remediation_factory.ai_fixes_generated || 0} 
              subtitle="Generated" 
              icon={Brain} 
              color="pink"
            />
            <MetricCard 
              title="Critical Priority" 
              value={operationalData.remediation_factory.tasks_by_priority?.critical || 0} 
              subtitle="Urgent tasks" 
              icon={AlertTriangle} 
              color="red"
            />
          </div>

          {/* Task Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-700/50' : 'bg-slate-50'}`}>
              <h4 className={`font-inter text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Task Pipeline Status
              </h4>
              <div className="space-y-2">
                {[
                  { key: 'pending', label: 'Pending', color: 'bg-zinc-500' },
                  { key: 'in_progress', label: 'In Progress', color: 'bg-blue-500' },
                  { key: 'awaiting_approval', label: 'Awaiting Approval', color: 'bg-yellow-500' },
                  { key: 'approved', label: 'Approved', color: 'bg-emerald-500' },
                  { key: 'deployed', label: 'Deployed', color: 'bg-indigo-500' },
                  { key: 'verified', label: 'Verified', color: 'bg-green-500' },
                ].map(status => {
                  const count = operationalData.remediation_factory.tasks_by_status?.[status.key] || 0;
                  const total = operationalData.remediation_factory.total_tasks || 1;
                  const percentage = Math.round((count / total) * 100);
                  
                  return (
                    <div key={status.key} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${status.color}`} />
                      <span className={`text-xs flex-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {status.label}
                      </span>
                      <span className={`text-xs font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {count}
                      </span>
                      <div className="w-20">
                        <div className={`h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-navy-600' : 'bg-slate-200'}`}>
                          <div 
                            className={`h-full rounded-full ${status.color}`} 
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-700/50' : 'bg-slate-50'}`}>
              <h4 className={`font-inter text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Priority Distribution
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'critical', label: 'Critical', color: 'red', bg: 'bg-red-500/20', text: 'text-red-400' },
                  { key: 'high', label: 'High', color: 'orange', bg: 'bg-orange-500/20', text: 'text-orange-400' },
                  { key: 'medium', label: 'Medium', color: 'yellow', bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
                  { key: 'low', label: 'Low', color: 'green', bg: 'bg-green-500/20', text: 'text-green-400' },
                ].map(priority => (
                  <div 
                    key={priority.key}
                    className={`p-3 rounded-lg text-center ${priority.bg}`}
                  >
                    <p className={`text-lg font-inter font-bold ${priority.text}`}>
                      {operationalData.remediation_factory.tasks_by_priority?.[priority.key] || 0}
                    </p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                      {priority.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== AUTOMATION CENTER METRICS ========== */}
      {automationData && (
        <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-violet-500/15 border border-violet-500/30' : 'bg-violet-100 border border-violet-200'}`}>
                <Workflow className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              </div>
              <div>
                <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Automation Center</h3>
                <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Orchestration & BAS Overview</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/automation')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isDarkMode 
                  ? 'bg-violet-500/20 text-violet-400 hover:bg-violet-500/30' 
                  : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
              }`}
            >
              View All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Orchestrator Status */}
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-700/50' : 'bg-slate-50'}`}>
              <h4 className={`font-inter text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Orchestrator Status
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Status</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    automationData.status?.enabled 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {automationData.status?.enabled ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Active Rules</span>
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {automationData.status?.active_rules || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Last Triggered</span>
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {automationData.status?.last_triggered 
                      ? new Date(automationData.status.last_triggered).toLocaleTimeString()
                      : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            {/* Recent BAS Simulations */}
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-700/50' : 'bg-slate-50'}`}>
              <h4 className={`font-inter text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Recent BAS Simulations
              </h4>
              {automationData.recentSimulations?.length > 0 ? (
                <div className="space-y-2">
                  {automationData.recentSimulations.slice(0, 3).map((sim, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className={`text-xs truncate max-w-[120px] ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {sim.target}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${
                        sim.overall_risk === 'critical' ? 'bg-red-500/20 text-red-400' :
                        sim.overall_risk === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        sim.overall_risk === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {sim.overall_risk}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>No recent simulations</p>
              )}
            </div>

            {/* Recent Automation Actions */}
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-700/50' : 'bg-slate-50'}`}>
              <h4 className={`font-inter text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Recent Actions
              </h4>
              {automationData.recentActions?.length > 0 ? (
                <div className="space-y-2">
                  {automationData.recentActions.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className={`text-xs truncate max-w-[100px] capitalize ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {action.action_type?.replace(/_/g, ' ') || 'Action'}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        action.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                        action.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {action.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>No recent actions</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
